import classNames from 'classnames';
import React, { Component, PropsWithChildren } from 'react';
import { ExternalClickWrapper } from '../ExternalClickWrapper/ExternalClickWrapper';
import { createId } from '../utils/IdGenerator';
import { code } from '../utils/keyCode';
import './DropdownButton.scss';
import { DropdownList } from './DropdownList';
import { triggerDefault, triggerEllipsis } from './DropdownTrigger';
import { IDropdownButtonProps } from './types';

export interface IDropdownButtonState {
  isOpen: boolean;
}

export const DropdownButtonContext = React.createContext<{
  releaseFocus: (state: boolean) => void;
}>({ releaseFocus: () => '' });

export class DropdownButton extends Component<PropsWithChildren<IDropdownButtonProps>, IDropdownButtonState> {
  // tslint:disable-next-line:member-access
  static defaultProps = {
    bsStyle: 'default',
    noCaret: false,
    releaseFocusAfterSelect: false,
    title: '',
  };

  public state = {
    isOpen: false,
  };

  public id: string;
  private elContainer: HTMLDivElement | null;
  private elTrigger: HTMLElement | null; // el for the trigger container
  private keepFocus: boolean;

  constructor(props: IDropdownButtonProps) {
    super(props);
    this.id = createId('trigger');
  }

  public componentDidMount() {
    window.document.addEventListener('keydown', this.handleKeyboardEvent);
    window.document.addEventListener('keydown', this.onEscape, true);
  }

  public componentWillUnmount() {
    window.document.removeEventListener('keydown', this.handleKeyboardEvent);
    window.document.removeEventListener('keydown', this.onEscape, true);
  }

  public render() {
    const {
      bsSize,
      bsStyle,
      disabled,
      noCaret,
      ellipsis,
      title,
      triggerRenderer = this.props.ellipsis ? triggerEllipsis : triggerDefault,
    } = this.props;
    const { isOpen } = this.state;
    const isCustomTrigger = !!this.props.triggerRenderer;
    const classes = classNames('hlc-dropdownButton dropdown', {
      ellipsis,
      open: this.state.isOpen,
    });
    const triggerClasses = classNames('hlc-dropdownbutton-trigger', {
      btn: !isCustomTrigger,
      [`btn-${bsStyle}`]: !isCustomTrigger,
      [`btn-${bsSize || ''}`]: !isCustomTrigger && bsSize ? true : false,
    });

    return (
      <ExternalClickWrapper
        disabled={!this.state.isOpen}
        onExternal={() => {
          this.closeDropdown();
        }}
      >
        <div className={classes} qa-id={this.props['qa-id']} ref={(e) => (this.elContainer = e)}>
          {triggerRenderer({
            'aria-expanded': isOpen,
            'aria-haspopup': true,
            className: triggerClasses,
            disabled,
            id: this.id,
            noCaret,
            onClick: this.toggleDropdown,
            ref: this.setTriggerRef,
            role: 'button',
            tabIndex: 0,
            title,
          })}
          {this.maybeRenderDropdownList()}
        </div>
      </ExternalClickWrapper>
    );
  }

  private maybeRenderDropdownList() {
    if (!this.state.isOpen) {
      return null;
    }

    const {
      align = 'left',
      dropdownListWidth = 185,
      maxHeight = 200,
      onSelect,
      releaseFocusAfterSelect,
      renderInfo,
      menuClassNames,
    } = this.props;

    return (
      <DropdownButtonContext.Provider value={{ releaseFocus: (state: boolean) => (this.keepFocus = state) }}>
        <DropdownList
          className={menuClassNames}
          align={align}
          width={dropdownListWidth}
          height={maxHeight}
          onClose={() => {
            const keepFocus = releaseFocusAfterSelect === false && this.keepFocus === true;
            this.closeDropdown(keepFocus);
          }}
          labeledById={this.id}
          elContainer={this.elContainer}
          onSelect={onSelect}
          renderInfo={renderInfo}
        >
          {this.props.children}
        </DropdownList>
      </DropdownButtonContext.Provider>
    );
  }

  private onEscape = (e: KeyboardEvent) => {
    if (this.state.isOpen && e.keyCode === 27) {
      this.closeDropdown(true);
      e.stopPropagation();
    }
  };

  // listening for up/down arrow, enter to open dropdownList
  private handleKeyboardEvent = (e: KeyboardEvent) => {
    const hasFocus = this.elTrigger === document.activeElement;
    if (this.state.isOpen || !hasFocus) {
      return;
    }

    const { keyCode: key } = e;
    if (key === code('enter') || this.isUpDown(key)) {
      this.openDropdown();
      e.preventDefault();
    }
  };

  private openDropdown = () => {
    this.setState((prevState) => {
      return prevState.isOpen ? null : { isOpen: true };
    });
  };

  private closeDropdown = (keepFocus?: boolean) => {
    this.setState(
      (prevState) => {
        return prevState.isOpen ? { isOpen: false } : null;
      },
      () => {
        keepFocus && this.elTrigger && this.elTrigger.focus();
      },
    );
  };

  private isUpDown(key: number) {
    return [code('up'), code('down')].indexOf(key) !== -1;
  }

  private setTriggerRef = (e: HTMLElement | null) => {
    this.elTrigger = e;
  };

  // toggle the dropdown menu
  private toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const method = this.state.isOpen ? 'close' : 'open';
    this[`${method}Dropdown`]();
  };
}

/**
 * We export DropdownButton as it is to support Styleguist's patterns.
 * And we export enhanced component as default for the actual usage
 * @see {@link https://react-styleguidist.js.org/docs/thirdparties.html}
 */

export default DropdownButton;
